import * as React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import type { SxProps } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

interface ChargeableConfirmationModalProps {
  open: boolean;
  handleClose: () => void;
  onChargeable: () => void;
  onNonChargeable: () => void;
  title?: string;
  contentMessage?: string | React.ReactNode;
  customContent?: React.ReactNode;
  chargeableText?: string;
  nonChargeableText?: string;
  centerActions?: boolean;
  fullWidthButtons?: boolean;
  chargeableButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  nonChargeableButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  actionsSx?: SxProps;
}

const chargeableButtonStyle = {
  backgroundColor: '#0C7DFF',
  color: 'white',
  '&:hover': {
    backgroundColor: '#0A69D9',
  },
  textTransform: 'none',
  minWidth: '120px',
};

const nonChargeableButtonStyle = {
  borderColor: '#E2E3E5',
  color: '#0F1928',
  '&:hover': {
    borderColor: '#C5C7CA',
    backgroundColor: '#F5F6FA',
  },
  textTransform: 'none',
  minWidth: '120px',
};

export function ChargeableConfirmationModal({
  open,
  handleClose,
  onChargeable,
  onNonChargeable,
  title = 'Consumable Service Not Configured',
  contentMessage = 'Consumable service is not configured for the selected package. Do you want this consumption to be chargeable or non-chargeable?',
  customContent,
  chargeableText = 'Chargeable',
  nonChargeableText = 'Non-Chargeable',
  centerActions,
  fullWidthButtons,
  chargeableButtonProps,
  nonChargeableButtonProps,
  actionsSx,
}: ChargeableConfirmationModalProps) {

  const ariaLabelledBy = customContent ? undefined : 'chargeable-confirmation-modal-title';
  const ariaDescribedBy = customContent ? undefined : 'chargeable-confirmation-modal-description';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: '10px', minWidth: '400px' } }}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {customContent ? (
        <DialogContent>{customContent}</DialogContent>
      ) : (
        <>
          <DialogTitle id="chargeable-confirmation-modal-title">
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: '#0F1928' }}>
              {title}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText 
              id="chargeable-confirmation-modal-description" 
              sx={{ color: '#5C6A7A' }}
            >
              {contentMessage}
            </DialogContentText>
          </DialogContent>
        </>
      )}
      <DialogActions
        sx={{
          justifyContent: centerActions ? 'center' : undefined,
          gap: 1,
          px: 3,
          pb: 2,
          ...(typeof actionsSx === 'object' && !Array.isArray(actionsSx) ? actionsSx : {}),
        }}
      >
        <Button
          onClick={() => {
            onNonChargeable();
            handleClose();
          }}
          variant={nonChargeableButtonProps?.variant ?? 'outlined'}
          color={nonChargeableButtonProps?.color}
          size={nonChargeableButtonProps?.size}
          startIcon={nonChargeableButtonProps?.startIcon}
          endIcon={nonChargeableButtonProps?.endIcon}
          sx={{
            ...(fullWidthButtons ? { flex: 1 } : {}),
            ...nonChargeableButtonStyle,
            ...(nonChargeableButtonProps?.sx && typeof nonChargeableButtonProps.sx === 'object' && !Array.isArray(nonChargeableButtonProps.sx) ? (nonChargeableButtonProps.sx as object) : {}),
          }}
          disabled={nonChargeableButtonProps?.disabled}
        >
          {nonChargeableText}
        </Button>
        <Button
          onClick={() => {
            onChargeable();
            handleClose();
          }}
          variant={chargeableButtonProps?.variant ?? 'contained'}
          color={chargeableButtonProps?.color}
          size={chargeableButtonProps?.size}
          startIcon={chargeableButtonProps?.startIcon}
          endIcon={chargeableButtonProps?.endIcon}
          sx={{
            ...(fullWidthButtons ? { flex: 1 } : {}),
            ...chargeableButtonStyle,
            ...(chargeableButtonProps?.sx && typeof chargeableButtonProps.sx === 'object' && !Array.isArray(chargeableButtonProps.sx) ? (chargeableButtonProps.sx as object) : {}),
          }}
          disabled={chargeableButtonProps?.disabled}
          autoFocus
        >
          {chargeableText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
